import { useEffect, useState } from 'react';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6okcnuancq2eie81vl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="px-8 lg:px-32 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="https://public.readdy.ai/ai/img_res/793aa9ba-effb-4e90-9976-5b1591252cf1.png" alt="FILLINE d.o.o." className="h-10 w-10 object-contain" />
              <h1 className={`text-lg font-medium transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>FILLINE d.o.o.</h1>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <button onClick={() => scrollToSection('home')} className={`px-5 py-2 text-sm transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Home</button>
              <span className={`text-xs ${scrolled ? 'text-gray-300' : 'text-white/30'}`}>|</span>
              <button onClick={() => scrollToSection('about')} className={`px-5 py-2 text-sm transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>About</button>
              <span className={`text-xs ${scrolled ? 'text-gray-300' : 'text-white/30'}`}>|</span>
              <button onClick={() => scrollToSection('products')} className={`px-5 py-2 text-sm transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Products</button>
              <span className={`text-xs ${scrolled ? 'text-gray-300' : 'text-white/30'}`}>|</span>
              <button onClick={() => scrollToSection('features')} className={`px-5 py-2 text-sm transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Features</button>
              <span className={`text-xs ${scrolled ? 'text-gray-300' : 'text-white/30'}`}>|</span>
              <button onClick={() => scrollToSection('contact')} className={`px-5 py-2 text-sm transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e1942055598a8b10d0dccf3aab81e94b.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 w-full px-8 lg:px-32 py-32">
          <div className="max-w-4xl">
            <h2 className="text-7xl lg:text-8xl font-extralight text-white leading-tight mb-6">
              Modern<br />
              iOS Applications
            </h2>
            <p className="text-5xl lg:text-6xl font-light text-white/95 leading-tight mb-8">
              For Enhanced<br />
              Security
            </p>
            <p className="text-xl text-white/80 max-w-2xl leading-relaxed">
              A team of experts developing cutting-edge applications for the iOS platform, designed to enhance user experience and improve mobile device security.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-8 lg:px-32 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="text-xs font-semibold tracking-widest text-teal-600 uppercase">Who We Are</span>
          </div>
          <div className="grid md:grid-cols-2 gap-20">
            <div>
              <h3 className="text-5xl font-bold text-gray-900 leading-tight mb-6">FILLINE d.o.o.</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                We are a dedicated team of iOS development specialists committed to creating innovative mobile solutions that prioritize user experience and security.
              </p>
            </div>
            <div>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our expertise lies in developing sophisticated applications for the iOS platform. We focus on creating products that not only meet the highest standards of functionality but also provide enhanced security features for mobile devices.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With years of experience in mobile app development, we understand the evolving needs of iOS users and continuously innovate to deliver solutions that make a real difference in their daily digital lives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-8 lg:px-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <span className="text-xs font-semibold tracking-widest text-teal-600 uppercase mb-4 block">Our Products</span>
            <h3 className="text-6xl font-bold text-gray-900 mb-6">Innovative iOS Solutions</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover our flagship applications designed to enhance your mobile experience with cutting-edge technology and user-centric design.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* VPN Product */}
            <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-900 to-slate-800 h-[720px] flex flex-col">
              <div className="flex-1 p-10 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20VPN%20mobile%20application%20interface%20screenshot%20on%20iPhone%20displaying%20secure%20connection%20status%20with%20shield%20icon%2C%20clean%20minimalist%20UI%20design%2C%20dark%20purple%20and%20blue%20gradient%20background%2C%20professional%20security%20app%20aesthetic%2C%20connection%20toggle%20button%2C%20server%20location%20selector%2C%20encryption%20indicators%2C%20contemporary%20iOS%20design%20language&width=400&height=600&seq=vpn-product-001&orientation=portrait" 
                  alt="VPN Application" 
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
              <div className="p-10 bg-black/20">
                <div className="flex items-start justify-between mb-6">
                  <span className="text-sm font-semibold tracking-wider text-white uppercase">VPN</span>
                  <div className="flex gap-1">
                    <div className="w-1 h-1 rounded-full bg-white/60"></div>
                    <div className="w-1 h-1 rounded-full bg-white/60"></div>
                    <div className="w-1 h-1 rounded-full bg-white/60"></div>
                  </div>
                </div>
                <h4 className="text-3xl font-semibold text-white mb-8 leading-tight">
                  Secure your connection with military-grade encryption
                </h4>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-base font-medium text-white">Secure Connection</p>
                    <p className="text-sm text-white/60">Privacy Protection</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="rounded-3xl overflow-hidden bg-emerald-100 h-[720px] flex flex-col transform md:-translate-y-16">
              <div className="flex-1 p-10 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-8 w-full h-full flex items-center justify-center shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20iOS%20cleaner%20application%20interface%20showing%20storage%20optimization%20screen%20with%20circular%20progress%20indicator%2C%20file%20categories%20with%20colorful%20icons%2C%20clean%20white%20background%2C%20minimalist%20design%2C%20storage%20usage%20visualization%2C%20delete%20and%20optimize%20buttons%2C%20contemporary%20mobile%20app%20UI%2C%20professional%20and%20friendly%20aesthetic&width=400&height=600&seq=cleaner-product-001&orientation=portrait" 
                    alt="Cleaner Application" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="p-10">
                <span className="text-xs font-semibold tracking-wider text-emerald-800 uppercase block mb-4">Cleaner</span>
                <h4 className="text-3xl font-semibold text-gray-900 leading-tight mb-4">
                  Optimize your device performance effortlessly
                </h4>
                <div className="w-16 h-1 bg-emerald-600"></div>
              </div>
            </div>

            {/* Technology Card */}
            <div className="rounded-3xl overflow-hidden bg-amber-50 h-[720px] flex flex-col p-10 relative">
              <div className="absolute inset-0 opacity-5">
                <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)', backgroundSize: '10px 10px' }}></div>
              </div>
              <div className="relative flex-1 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-2xl bg-gray-900 flex items-center justify-center mb-8">
                  <i className="ri-code-s-slash-line text-white text-4xl"></i>
                </div>
                <h4 className="text-4xl font-bold text-gray-900 mb-6">iOS Platform</h4>
                <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-sm">
                  Built exclusively for iOS using the latest technologies and frameworks to ensure optimal performance and seamless integration with Apple ecosystem.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <span className="text-sm text-gray-600 font-medium">Swift</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600 font-medium">SwiftUI</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600 font-medium">Core Data</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-8 lg:px-32 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h3 className="text-7xl font-extralight text-white mb-6">Expertise & Capabilities</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-star-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">Enhanced Security</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                We implement industry-leading security protocols and encryption standards to protect user data and ensure privacy across all our applications.
              </p>
            </div>

            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">User Experience</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                Our design philosophy centers on creating intuitive, elegant interfaces that make complex functionality accessible to users of all technical levels.
              </p>
            </div>

            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">Performance Optimization</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                Every application is optimized for speed and efficiency, ensuring smooth operation even on older devices while maximizing battery life.
              </p>
            </div>

            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">Regular Updates</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                We continuously improve our products with regular updates, new features, and compatibility enhancements to stay ahead of iOS platform evolution.
              </p>
            </div>

            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">Dedicated Support</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                Our team provides comprehensive support to ensure users get the most out of our applications, with responsive assistance when needed.
              </p>
            </div>

            <div className="border border-white/10 rounded-2xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-5xl"></i>
              </div>
              <h4 className="text-2xl font-semibold text-white mb-4">Global Reach</h4>
              <p className="text-base text-gray-400 leading-relaxed">
                Our applications are designed for a worldwide audience, with support for multiple languages and compliance with international standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-8 lg:px-32 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-20 mb-20">
            <div>
              <h3 className="text-8xl font-bold text-white mb-12 leading-tight">FILLINE<br />d.o.o.</h3>
              <p className="text-base text-gray-400 leading-relaxed max-w-md mb-12">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            <div className="space-y-12">
              <div>
                <span className="text-xs font-semibold tracking-widest text-white uppercase block mb-4">Address</span>
                <p className="text-base text-white leading-relaxed">
                  Kidriceva ulica 25<br />
                  Celje, 3000<br />
                  Slovenia
                </p>
              </div>

              <div>
                <span className="text-xs font-semibold tracking-widest text-white uppercase block mb-4">Contact</span>
                <p className="text-base text-white">+1 4026626739</p>
              </div>

              <div>
                <span className="text-xs font-semibold tracking-widest text-white uppercase block mb-4">Email</span>
                <p className="text-base text-white">info@fillinedoo.com</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="border-t border-white/10 pt-20">
            <div className="max-w-3xl">
              <h4 className="text-4xl font-bold text-white mb-4">Get in Touch</h4>
              <p className="text-base text-gray-400 mb-12">Send us a message and we'll get back to you as soon as possible.</p>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">Subject</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    required 
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors text-sm"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-white mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    required 
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors resize-none text-sm"
                    placeholder="Your message (max 500 characters)"
                  ></textarea>
                </div>
                <div>
                  <button 
                    type="submit" 
                    className="px-8 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 mt-20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600">© 2025 FILLINE d.o.o.</p>
              <div className="flex items-center gap-10">
                <span className="text-sm text-gray-600">Privacy</span>
                <span className="text-sm text-gray-600">Terms</span>
                <span className="text-sm text-gray-600">Cookies</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;